import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import useSeo from "@/hooks/useSeo";
import { CATEGORIES } from "@/mocks/archive";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dagkgl1tto0ceqqnhu30";

const VALUES = [
  { icon: "ri-compass-3-line", t: "定位", d: "黑料档案定位为一套面向娱乐资讯读者的结构化争议检索库，用「分类档案」替代碎片化吃瓜。" },
  { icon: "ri-tools-line", t: "理念", d: "我们相信真正的信息价值在于脉络，而不是速度。先分类、后叙事，让每一条瓜都经得起按图索骥。" },
  { icon: "ri-scales-3-line", t: "边界", d: "严守公序良俗与信息真实性底线：不传播隐私细节、不人肉素人、不把未经证实的内容写成结论。" },
  { icon: "ri-heart-pulse-line", t: "责任", d: "娱乐之余保持理性提醒：一切争议最终以司法与官方披露为准，围观不越界，讨论不伤及无辜。" },
];

const QUOTES = [
  { n: "阿茶", role: "夜刷瓜友", text: "以前吃瓜全靠刷热搜拼时间线，现在按分类一搜就有整套梳理，爽。" },
  { n: "老K", role: "影视从业者", text: "作为行业里干活的人，看这些档案其实是在提醒自己：作品之外，艺德和契约同样重要。" },
  { n: "Momo", role: "编辑部志愿者", text: "最喜欢那种克制——有瓜、有源、有边界，不为了流量把人往死里锤。" },
];

const STATS = [
  ["6", "大分类体系"],
  ["120+", "入库条目"],
  ["40+", "细分检索标签"],
  ["24h", "热点跟进窗口"],
];

export default function About() {
  const [form, setForm] = useState<{ status: "idle" | "loading" | "done"; msg: string }>({ status: "idle", msg: "" });

  async function onTip(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = e.currentTarget;
    const fd = new FormData(f);
    const hp = String(fd.get("website_alt") || "").trim();
    if (hp) {
      setForm({ status: "done", msg: "线索已收到，感谢你的贡献，编辑部会尽快核查。" });
      f.reset();
      return;
    }
    fd.delete("website_alt");
    setForm({ status: "loading", msg: "" });
    try {
      const res = await fetch(SUBMIT_ADDR, { method: "POST", body: new URLSearchParams(fd as unknown as Record<string, string>) });
      const text = await res.text();
      let parsed: any = null;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text || "";
      if (res.ok && (parsed?.code === "OK" || !parsed) && !/spam|form data is spam/i.test(String(serverMsg))) {
        setForm({ status: "done", msg: "线索已收到，感谢你的贡献！编辑部会优先核查并安排入库。" });
        f.reset();
      } else {
        setForm({ status: "idle", msg: String(serverMsg && serverMsg !== text ? serverMsg : (parsed?.meta?.detail || "提交失败，请稍后再试。")) });
      }
    } catch {
      setForm({ status: "idle", msg: "网络异常，线索未能送达，请稍后重试。" });
    }
  }

  useSeo({
    title: "关于我们 - 黑料档案编辑理念与定位",
    description: "了解黑料档案的平台定位、发展理念与内容原则。我们以分类立档的方式梳理明星黑料，坚持信息求真、克制表达与开放纠错。",
    keywords: "明星黑料,黑料档案,关于我们,内容原则,爆料投稿",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于黑料档案",
        inLanguage: "zh-CN",
        url: "https://gjkmv.cn/about",
        mainEntity: { "@type": "Organization", name: "黑料档案 · 八卦志" },
      },
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "联系黑料档案与线索投稿",
        inLanguage: "zh-CN",
        url: "https://gjkmv.cn/about#submit",
      },
    ],
  });

  return (
    <PageShell>
      {/* 顶部 */}
      <section className="bg-background-50 pt-28 md:pt-32 pb-8">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <nav className="flex items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800 whitespace-nowrap">关于我们</span>
          </nav>
          <div className="mt-6 grid gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <p className="flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-primary-600 uppercase">
                <span className="h-px w-8 bg-primary-400" /> About Us
              </p>
              <h1 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
                关于<span className="text-primary-600">黑料档案</span>
              </h1>
              <p className="mt-4 text-sm md:text-base leading-8 text-foreground-600">
                我们是一支专注娱乐圈舆情整理的内容编辑团队，运营面向华语观众的明星黑料分类档案库。
                我们不制造热度，只做一件事——把纷繁的娱乐争议，整理成一套可以<b className="text-foreground-900">分类、检索、追溯</b>的信息档案。
              </p>
              <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {STATS.map(([n, l]) => (
                  <div key={l} className="rounded-lg border border-background-300/70 bg-background-100 p-3 text-center">
                    <p className="font-heading text-xl font-bold text-primary-700">{n}</p>
                    <p className="mt-1 text-[11px] text-foreground-500">{l}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-background-300/70 bg-background-100 p-6 md:p-8">
              <div className="flex items-center gap-2">
                <span className="grid place-items-center w-9 h-9 rounded-lg bg-primary-600 text-background-50"><i className="ri-focus-3-line text-lg" /></span>
                <div>
                  <p className="font-heading text-base font-bold text-foreground-950">黑料档案 · 八卦志</p>
                  <p className="text-[11px] tracking-[0.18em] text-foreground-400 uppercase">Scandal Archive Studio</p>
                </div>
              </div>
              <blockquote className="mt-5 border-l-2 border-primary-500 pl-4 text-sm leading-7 text-foreground-600 italic">
                「吃瓜不该是一场疲惫的追踪，而应是一份可以随手翻开的档案。让每一次围观，都离事实更近一点。」
              </blockquote>
              <p className="mt-4 text-xs text-foreground-400">—— 编辑部写在档案库上线时</p>
            </div>
          </div>
        </div>
      </section>

      {/* 定位 / 理念 / 原则 */}
      <section id="principles" className="bg-background-100 border-y border-background-300/70 py-12 md:py-16 scroll-mt-24">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <div key={v.t} className={`rounded-xl border border-background-300/70 bg-background-50 p-5 ${i === 2 ? "lg:-translate-y-2" : ""}`}>
                <span className={`grid place-items-center w-11 h-11 rounded-xl ${i === 1 ? "bg-accent-100 text-accent-700" : "bg-primary-100 text-primary-700"}`}>
                  <i className={`${v.icon} text-lg`} />
                </span>
                <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">{v.t}</h2>
                <p className="mt-2 text-sm leading-6 text-foreground-600">{v.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-[1.2fr_1fr] lg:items-start">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">我们的内容原则</h2>
              <div className="mt-5 space-y-4">
                {[
                  ["01", "来源可溯", "素材优先采用公开报道、官方声明与具备公共性的信息渠道，并在条目中标注事件时间与分类，杜绝无来源的「空穴来风」。", "primary"],
                  ["02", "分类立档", "用六大分类与细分标签建立检索秩序，一条事件归入对应档案并持续补充，让信息随事件推进而演进。", "accent"],
                  ["03", "克制表达", "不使用耸动、煽动、猎奇式表达；不人肉素人；不对未经司法定性的内容妄下结论，始终保留开放态度。", "primary"],
                  ["04", "开放纠错", "如发现事实偏差或不当措辞，欢迎通过投稿表单反馈，我们将在核查后及时修正或下线相关条目。", "accent"],
                ].map(([no, t, d, c]) => (
                  <div key={no} className="flex gap-4 rounded-xl border border-background-300/60 bg-background-50 p-4">
                    <span className={`shrink-0 font-heading text-2xl font-black ${c === "accent" ? "text-accent-600" : "text-primary-600"}`}>{no}</span>
                    <div>
                      <h3 className="font-medium text-foreground-950">{t}</h3>
                      <p className="mt-1 text-sm leading-6 text-foreground-600">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 口碑 */}
            <aside className="rounded-2xl border border-background-300/70 bg-background-50 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">读者怎么说</h2>
              <div className="mt-4 space-y-4">
                {QUOTES.map((q) => (
                  <figure key={q.n} className="rounded-xl bg-background-100 p-4">
                    <blockquote className="text-sm leading-6 text-foreground-700">「{q.text}」</blockquote>
                    <figcaption className="mt-3 flex items-center gap-2 text-xs">
                      <span className="grid place-items-center w-6 h-6 rounded-full bg-primary-600 text-background-50 text-[10px] font-bold">{q.n.slice(0, 1)}</span>
                      <span className="font-medium text-foreground-900">{q.n}</span>
                      <span className="text-foreground-400">· {q.role}</span>
                    </figcaption>
                  </figure>
                ))}
              </div>
              <p className="mt-4 text-[11px] text-foreground-400">以上评价摘录自读者自愿反馈，为保护隐私已匿名化处理。</p>
            </aside>
          </div>
        </div>
      </section>

      {/* 联系 + 投稿 */}
      <section id="submit" className="bg-background-50 py-12 md:py-16 scroll-mt-24">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6 grid gap-10 lg:gap-16 lg:grid-cols-2">
          <div>
            <p className="flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-primary-600 uppercase">
              <span className="h-px w-8 bg-primary-400" /> Contact &amp; Submit
            </p>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950">联系编辑团队 / 提交线索</h2>
            <p className="mt-4 text-sm md:text-base leading-7 text-foreground-600">
              如果你手上有需要补充进档案库的公开线索，或对某条档案有纠错建议，欢迎通过右侧表单提交。
              请尽量附上<b className="text-foreground-900">可核实的信息来源</b>，我们核对后会第一时间处理。
            </p>
            <div className="mt-6 space-y-3">
              {[
                { i: "ri-customer-service-2-line", t: "编辑部邮箱", v: "editor@xingyuanhei.com" },
                { i: "ri-time-line", t: "响应时效", v: "工作日 24 小时内回复" },
                { i: "ri-checkbox-circle-line", t: "收录规则", v: "需为公开可查的争议信息，不含人肉与隐私内容" },
              ].map((c) => (
                <div key={c.t} className="flex items-center gap-3 rounded-lg border border-background-300/60 bg-background-100 px-4 py-3">
                  <span className="grid place-items-center w-9 h-9 rounded-lg bg-accent-100 text-accent-700"><i className={`${c.i} text-base`} /></span>
                  <div>
                    <p className="text-[11px] text-foreground-400">{c.t}</p>
                    <p className="text-sm font-medium text-foreground-900">{c.v}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 投稿表单 */}
          <form data-readdy-form="明星黑料线索投稿" onSubmit={onTip} className="rounded-2xl border border-background-300/70 bg-background-100 p-5 md:p-7">
            <div className="flex items-center gap-2">
              <span className="grid place-items-center w-9 h-9 rounded-lg bg-primary-600 text-background-50"><i className="ri-lightbulb-flash-line text-lg" /></span>
              <div>
                <h3 className="font-heading text-base font-bold text-foreground-950">线索投稿表</h3>
                <p className="text-xs text-foreground-400">带 * 为必填项</p>
              </div>
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="tip-name" className="text-xs text-foreground-500">称呼</label>
                <input id="tip-name" name="name" type="text" placeholder="如何称呼你" className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:ring-2 focus:ring-primary-400" />
              </div>
              <div>
                <label htmlFor="tip-email" className="text-xs text-foreground-500">常用邮箱 *</label>
                <input id="tip-email" name="email" type="email" required placeholder="用于接收处理结果" className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:ring-2 focus:ring-primary-400" />
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="tip-cat" className="text-xs text-foreground-500">线索所属分类 *</label>
              <div className="relative mt-1.5">
                <select id="tip-cat" name="category" required defaultValue="renpin" className="w-full appearance-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 pr-9 text-sm text-foreground-900 outline-none focus:ring-2 focus:ring-primary-400">
                  {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}黑料</option>)}
                  <option value="other">其他 / 综合</option>
                </select>
                <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400" />
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="tip-msg" className="text-xs text-foreground-500">线索说明（附信息来源）*</label>
              <textarea id="tip-msg" name="message" required maxLength={500} rows={5} placeholder="请描述事件脉络，并尽量附上可核实的公开信息来源链接或时间节点…" className="mt-1.5 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:ring-2 focus:ring-primary-400" />
            </div>

            <input name="website_alt" type="text" className="input-protection" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

            <button
              type="submit"
              disabled={form.status === "loading"}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-600 px-5 py-3 text-sm font-semibold text-background-50 hover:bg-primary-700 transition-colors whitespace-nowrap disabled:opacity-70"
            >
              {form.status === "loading" ? "提交中…" : "提交线索"}
              {form.status !== "loading" && <i className="ri-send-plane-line text-sm" />}
            </button>
            {form.msg && (
              <p className={`mt-3 text-xs ${form.status === "done" ? "text-accent-700" : "text-primary-700"}`} role="status">
                <i className="ri-information-line mr-1" />
                {form.msg}
              </p>
            )}
            <p className="mt-3 text-[11px] leading-5 text-foreground-400">
              你的邮箱仅用于回复处理结果。请勿在表单中提交任何他人隐私、人身攻击或未经证实的内容。
            </p>
          </form>
        </div>
      </section>
    </PageShell>
  );
}